import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardHeader from '@mui/material/CardHeader'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ageMinutes, type AtisKind, type AtisTextBroadcast } from '@jupi/shared'
import { formatClockTime, formatTime, RemoteContent, useServerClock } from '@jupi/ui'
import { useAtisBroadcast } from '../../api/atis'
import { AtisScreens } from './AtisScreens'
import { cloudsText, temperatureText, visibilityText, windText } from '../weather/weatherFormat'

const PHONETIC: Record<string, string> = {
  A: 'Alfa', B: 'Bravo', C: 'Charlie', D: 'Delta', E: 'Echo', F: 'Foxtrot', G: 'Golf', H: 'Hotel',
  I: 'India', J: 'Juliett', K: 'Kilo', L: 'Lima', M: 'Mike', N: 'November', O: 'Oscar', P: 'Papa',
  Q: 'Quebec', R: 'Romeo', S: 'Sierra', T: 'Tango', U: 'Uniform', V: 'Victor', W: 'Whiskey', X: 'X-ray',
  Y: 'Yankee', Z: 'Zulu',
}

// An automated broadcast renews at least every 30 minutes.
const OLD_AFTER_MIN = 35

const mono = { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' } as const

export function AtisCard({ icao }: { icao: string }) {
  const { t } = useTranslation()
  const { data, isLoading, error } = useAtisBroadcast(icao)
  const { utcMs: now } = useServerClock()
  const station = data?.station

  return (
    <Card>
      <CardHeader
        avatar={<Icon icon="mdi:radio-tower" width={28} />}
        title={
          <Typography variant="h6" component="h2">
            {icao}
            {station ? ` – ${station.name}` : ''}
          </Typography>
        }
        subheader={
          station
            ? [
                station.callsign,
                station.frequencyMhz === null ? null : `${station.frequencyMhz.toFixed(3)} MHz`,
                t(`atis.kind.${station.kind}`),
              ]
                .filter(Boolean)
                .join(' · ')
            : null
        }
        sx={{ pb: 0 }}
      />
      <CardContent>
        <RemoteContent isLoading={isLoading} error={error}>
          {data?.broadcast?.format === 'text' ? (
            <Broadcast broadcast={data.broadcast} kind={data.station.kind} now={now} />
          ) : data?.broadcast?.format === 'screen' ? (
            <AtisScreens broadcast={data.broadcast} now={now} />
          ) : null}
        </RemoteContent>
      </CardContent>
    </Card>
  )
}

function Broadcast({ broadcast: b, kind, now }: { broadcast: AtisTextBroadcast; kind: AtisKind; now: number }) {
  const { t } = useTranslation()
  const age = ageMinutes(b.issuedAt, now)
  const old = age > OLD_AFTER_MIN
  const r = b.report
  const letterName = b.letter ? (PHONETIC[b.letter.toUpperCase()] ?? b.letter) : null

  const wind = windText(r.windDirectionDeg, r.windSpeedKt, r.windGustKt, t('weather.calm'))
  const variable =
    r.windVariableFromDeg !== null && r.windVariableToDeg !== null
      ? ` ${t('weather.variableBetween', { from: r.windVariableFromDeg, to: r.windVariableToDeg })}`
      : ''
  const rows: [string, string | null][] = [
    [t('weather.wind'), wind === null ? null : wind + variable],
    [t('weather.visibility'), r.cavok ? 'CAVOK' : visibilityText(r.visibilityM)],
    [t('weather.clouds'), r.cavok ? null : cloudsText(r.clouds)],
    [t('weather.temperatureDewPoint'), temperatureText(r.temperatureC, r.dewPointC)],
    [t('weather.qnh'), r.qnhHpa === null ? null : `${r.qnhHpa} hPa`],
  ]
  if (r.verticalVisibilityFt !== null) rows.push([t('weather.taf.verticalVisibility'), `${r.verticalVisibilityFt} ft`])

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        {b.letter ? (
          <Box
            sx={{
              minWidth: 56,
              height: 56,
              px: 1,
              borderRadius: 2,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label={t('atis.information', { letter: letterName })}
          >
            <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{b.letter}</Typography>
            <Typography sx={{ fontSize: 10, letterSpacing: 0.5 }}>{letterName?.toUpperCase()}</Typography>
          </Box>
        ) : null}
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {letterName ? t('atis.information', { letter: letterName }) : t('atis.title')}
          </Typography>
          <Typography variant="body2" color={old ? 'warning.main' : 'text.secondary'}>
            {formatClockTime(b.issuedAt, 'UTC')} UTC · {t('weather.minutesAgo', { count: age })}
          </Typography>
        </Box>
        {old ? <Chip size="small" color="warning" icon={<Icon icon="mdi:alert-outline" />} label={t('atis.old')} /> : null}
        {b.stale ? (
          <Chip
            size="small"
            color="warning"
            icon={<Icon icon="mdi:alert-outline" />}
            label={b.staleSince ? t('weather.staleSince', { time: formatTime(b.staleSince) }) : t('weather.stale')}
          />
        ) : null}
      </Stack>

      {b.audioPath ? (
        // Remount on a new recording, or the player keeps the old letter.
        <Box component="audio" key={b.audioPath} controls preload="none" src={b.audioPath} sx={{ width: '100%' }}>
          {t('atis.noAudio')}
        </Box>
      ) : null}

      <Box
        component="pre"
        sx={{
          ...mono,
          m: 0,
          p: 1.5,
          borderRadius: 1,
          bgcolor: 'action.hover',
          fontSize: '0.85rem',
          whiteSpace: 'pre-wrap',
        }}
      >
        {b.message.join('\n')}
      </Box>

      <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, m: 0 }}>
        {rows
          .filter(([, value]) => value !== null)
          .map(([label, value]) => (
            <Box key={label} sx={{ display: 'contents' }}>
              <Typography component="dt" variant="body2" color="text.secondary">
                {label}
              </Typography>
              <Typography component="dd" variant="body1" sx={{ m: 0, fontWeight: 500 }}>
                {value}
              </Typography>
            </Box>
          ))}
      </Box>

      <Typography variant="caption" color="text.secondary">
        {t(`atis.note.${kind}`)}
      </Typography>
    </Stack>
  )
}
